import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * Adds a free sample to the cart from the cart drawer/page.
 *
 * Behavior is driven by the theme's declarative `on:click="/add/{variantId}"`
 * binding, so it survives cart AJAX re-renders without any document-level
 * listeners of its own. Dispatches the native `CartAddEvent` (with rendered
 * cart sections) so `cart-items-component` morphs in place.
 *
 * @extends {Component}
 */
class CartSamplesComponent extends Component {
  /**
   * Adds a sample variant to the cart.
   *
   * @param {number} variantId - The variant id, parsed from the `on:click` binding.
   * @param {Event} event - The originating click event.
   */
  async add(variantId, event) {
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!variantId || button?.disabled) return;

    if (button) button.disabled = true;

    // Collect cart section ids so the response includes rendered HTML to morph.
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);

    // Use FormData so the browser sets the correct content-type. fetchConfig('javascript')
    // strips the JSON content-type header, so a JSON-string body would 400.
    const body = new FormData();
    body.set('id', variantId.toString());
    body.set('quantity', '1');
    body.set('sections', sectionIds.join(','));

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('javascript', { body })
      ).then((r) => r.json());

      if (response.status) {
        this.dispatchEvent(
          new CartErrorEvent(this.id, response.message, response.description, response.errors)
        );
        if (button) button.disabled = false;
        return;
      }

      this.dispatchEvent(
        new CartAddEvent(response, this.id, {
          source: 'cart-samples-component',
          variantId: variantId.toString(),
          sections: response.sections,
        })
      );
    } catch (error) {
      console.error(error);
      if (button) button.disabled = false;
    }
  }
}

if (!customElements.get('cart-samples-component')) {
  customElements.define('cart-samples-component', CartSamplesComponent);
}
